import React from 'react'

/**
 * Shared presentational helpers for the Instance Admin console.
 *
 * These started as file-local helpers in `InstanceAdminPage.tsx`. The Billing
 * tab is a directory of its own, so they moved here rather than being copied —
 * one definition means the new panels look identical to the existing ones,
 * automatically, forever.
 */

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 style={{ fontSize: '1.0625rem', marginBottom: '0.75rem' }}>{title}</h3>
      {children}
    </div>
  )
}

export function Field({ label, children, hint }: {
  label: string; children: React.ReactNode; hint?: string
}) {
  return (
    <label style={{ display: 'block', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
      <span style={{ display: 'block', marginBottom: '0.25rem' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.8125rem', opacity: 0.85 }}>
          {hint}
        </span>
      )}
    </label>
  )
}

export function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: '0.875rem',
      color: 'var(--color-text-secondary)',
      background: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border)',
      borderRadius: 6,
      padding: '0.75rem 0.875rem',
      lineHeight: 1.5,
    }}>
      {children}
    </div>
  )
}

export const chipStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  padding: '0.125rem 0.5rem',
  borderRadius: '999px',
  background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text-secondary)',
  whiteSpace: 'nowrap',
}

export const tableStyle: React.CSSProperties = {
  width: '100%',
  marginTop: '0.875rem',
  borderCollapse: 'collapse',
  fontSize: '0.875rem',
}

export function Th({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return (
    <th style={{
      textAlign: right ? 'right' : 'left', padding: '0.4rem 0.5rem',
      borderBottom: '1px solid var(--color-border)',
      color: 'var(--color-text-secondary)', fontWeight: 600,
    }}>
      {children}
    </th>
  )
}

export function Td({ children, right, style }: {
  children: React.ReactNode; right?: boolean; style?: React.CSSProperties
}) {
  return (
    <td style={{
      textAlign: right ? 'right' : 'left', padding: '0.4rem 0.5rem',
      borderBottom: '1px solid var(--color-border)', ...style,
    }}>
      {children}
    </td>
  )
}

/** Pull an API error's `detail` out, with a fallback. */
export function errText(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

/**
 * A modal shell matching the console's existing dialogs. `wide` is for the
 * plan editor, which carries identity + pricing + limits + features at once.
 */
export function AdminModal({ title, onClose, children, wide }: {
  title: string; onClose: () => void; children: React.ReactNode; wide?: boolean
}) {
  return (
    <div className="overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div
        className="modal animate-slide-up"
        style={{ maxWidth: wide ? 720 : 460, width: '95%', maxHeight: '85vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header"><h3 className="modal-title">{title}</h3></div>
        {children}
      </div>
    </div>
  )
}

/**
 * A limit input where BLANK means unlimited and 0 means "none allowed".
 *
 * That distinction is the whole reason this exists as its own control: a plain
 * number input makes an admin who wants "no limit" type 0, which would forbid
 * the thing entirely. The placeholder says "Unlimited" and the hint spells out
 * what 0 does.
 */
export function LimitInput({ value, onChange, noun }: {
  value: number | null
  onChange: (v: number | null) => void
  noun: string
}) {
  return (
    <div>
      <input
        type="number"
        min={0}
        value={value === null || value === undefined ? '' : value}
        placeholder="Unlimited"
        onChange={(e) => {
          const raw = e.target.value.trim()
          onChange(raw === '' ? null : Math.max(0, parseInt(raw, 10) || 0))
        }}
        style={{ width: '100%' }}
      />
      <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
        {value === null || value === undefined
          ? `No limit on ${noun}.`
          : value === 0
            ? `0 means NO ${noun} are allowed at all.`
            : `Up to ${value.toLocaleString()} ${noun}.`}
      </span>
    </div>
  )
}
